import { ui } from '@procuros/datachecks';
import { produce } from 'immer';

import { doesPathMatchField } from 'services/schemableFormNavigation/uiConfigPathHelpers';
import { readQuantity } from 'support/helpers/packingList/packableQuantity';

import { getDefaultChildUnitType, sortUnitTypeOptions } from '../helpers/unitTypeHierarchy';
import {
  LineItemId,
  PackingListSeed,
  PackingListTree,
  TransportUnitId,
  TransportUnitNode,
  UnitTypeOption,
} from '../types';
import { initializeFromData } from './initializeFromData';

export type { UnitTypeOption };

export type PackingAction =
  | {
      type: 'CREATE_TU_WITH_ITEM';
      lineItemId: LineItemId;
      tuId: TransportUnitId;
      unitTypeOptions: Array<UnitTypeOption>;
      quantity: number;
    }
  | {
      type: 'CREATE_SUB_UNIT';
      parentTuId: TransportUnitId;
      lineItemId: LineItemId;
      childTuId: TransportUnitId;
      tuId: TransportUnitId;
      unitTypeOptions: Array<UnitTypeOption>;
      quantity: number;
    }
  | { type: 'PLACE_ITEM'; lineItemId: LineItemId; tuId: TransportUnitId; quantity: number }
  | {
      type: 'MOVE_ITEM';
      lineItemId: LineItemId;
      fromTuId: TransportUnitId;
      toTuId: TransportUnitId;
      quantity: number;
    }
  | { type: 'REMOVE_ITEM'; lineItemId: LineItemId; tuId: TransportUnitId }
  | { type: 'DELETE_TU'; tuId: TransportUnitId }
  | { type: 'SET_UNIT_TYPE'; tuId: TransportUnitId; unitType: string }
  | { type: 'SET_ITEM_QUANTITY'; tuId: TransportUnitId; lineItemId: LineItemId; quantity: number };

export const initialize = ({
  lineItems,
  transportUnits,
  quantityFieldPath = 'shipped_quantity',
}: PackingListSeed): PackingListTree => initializeFromData(lineItems, transportUnits, quantityFieldPath);

// Sorted (see sortUnitTypeOptions) so a consumer reading this array's first entry — a fresh
// unit's default type — gets the highest priority choice, not just the spec's declaration order.
export const getUnitTypeOptions = (fields: ui.TransportUnitsSection['fields']): Array<UnitTypeOption> =>
  sortUnitTypeOptions(getUnitTypeEnumOptions(fields).map((option) => ({ value: option.value, label: option.label })));

export const packingListReducer = (model: PackingListTree, action: PackingAction): PackingListTree =>
  produce(model, (draft) => {
    switch (action.type) {
      case 'CREATE_TU_WITH_ITEM': {
        // A root unit has no parent to stay under, so its default is simply the highest
        // priority option available.
        const unitType = getDefaultChildUnitType(action.unitTypeOptions, undefined) ?? '';
        draft.transportUnits.push({
          id: action.tuId,
          unitType,
          sscc: undefined,
          contents: [{ lineItemId: action.lineItemId, quantity: action.quantity }],
          children: [],
        });
        break;
      }
      case 'CREATE_SUB_UNIT': {
        const parent = findTransportUnitNode(draft.transportUnits, action.parentTuId);
        if (!parent) {
          break;
        }
        // The default is the most preferred type this specific parent may hold (a carton under
        // a pallet, not a package). Falling back to the parent's own type is unreachable in
        // practice: the "new sub unit" drop zone is hidden whenever no such type exists.
        const unitType = getDefaultChildUnitType(action.unitTypeOptions, parent.unitType) ?? parent.unitType;
        // Leaf-only rule: a parent that still holds items spills them into a first sub-unit, so
        // it can become a pure container before gaining the new sub-unit. The spilled sibling
        // gets the same type as the new one.
        if (parent.contents.length > 0) {
          parent.children.push(makeNode(action.childTuId, unitType, parent.contents));
          parent.contents = [];
        }
        parent.children.push(
          makeNode(action.tuId, unitType, [{ lineItemId: action.lineItemId, quantity: action.quantity }]),
        );
        break;
      }
      case 'PLACE_ITEM': {
        const transportUnit = findTransportUnitNode(draft.transportUnits, action.tuId);
        if (transportUnit) {
          addContent(transportUnit, action.lineItemId, action.quantity);
        }
        break;
      }
      case 'MOVE_ITEM': {
        moveItem(draft.transportUnits, action);
        break;
      }
      case 'REMOVE_ITEM': {
        const transportUnit = findTransportUnitNode(draft.transportUnits, action.tuId);
        if (transportUnit) {
          transportUnit.contents = transportUnit.contents.filter((content) => content.lineItemId !== action.lineItemId);
        }
        break;
      }
      case 'DELETE_TU': {
        removeTransportUnitNode(draft.transportUnits, action.tuId);
        break;
      }
      case 'SET_UNIT_TYPE': {
        const node = findTransportUnitNode(draft.transportUnits, action.tuId);
        if (node) {
          node.unitType = action.unitType;
        }
        break;
      }
      case 'SET_ITEM_QUANTITY': {
        const unit = findTransportUnitNode(draft.transportUnits, action.tuId);
        if (!unit) {
          break;
        }
        const content = unit.contents.find((item) => item.lineItemId === action.lineItemId);
        if (!content) {
          break;
        }
        if (action.quantity <= 0) {
          unit.contents = unit.contents.filter((item) => item.lineItemId !== action.lineItemId);
        } else {
          content.quantity = action.quantity;
        }
        break;
      }
    }
  });

export const getLeafUnitOptions = (
  transportUnits: Array<TransportUnitNode>,
  excludeTuId?: TransportUnitId,
): Array<{ tuId: TransportUnitId; position: string }> => {
  const options: Array<{ tuId: TransportUnitId; position: string }> = [];
  const walk = (nodes: Array<TransportUnitNode>, prefix: string): void => {
    nodes.forEach((node, index) => {
      const position = prefix ? `${prefix}.${index + 1}` : String(index + 1);
      if (node.children.length === 0) {
        if (node.id !== excludeTuId) {
          options.push({ tuId: node.id, position });
        }
      } else {
        walk(node.children, position);
      }
    });
  };
  walk(transportUnits, '');
  return options;
};

export const getPlacedQuantityByLineItem = (transportUnits: Array<TransportUnitNode>): Map<LineItemId, number> => {
  const placedByLineItem = new Map<LineItemId, number>();
  const gatherPlacedQuantity = (nodes: Array<TransportUnitNode>): void => {
    for (const transportUnit of nodes) {
      for (const content of transportUnit.contents) {
        placedByLineItem.set(content.lineItemId, (placedByLineItem.get(content.lineItemId) ?? 0) + content.quantity);
      }
      gatherPlacedQuantity(transportUnit.children);
    }
  };
  gatherPlacedQuantity(transportUnits);
  return placedByLineItem;
};

export const getUnitTotals = (
  model: PackingListTree,
  placedByLineItem: Map<LineItemId, number>,
  quantityFieldPath: string,
): { placed: number; total: number } => {
  let placed = 0;
  let total = 0;
  for (const lineItemEntry of Object.values(model.lineItems)) {
    if (lineItemEntry.row._internal?.parent_line_item) {
      continue;
    }
    const quantity = readQuantity(lineItemEntry.row[quantityFieldPath]);
    total += quantity;
    placed += Math.min(quantity, placedByLineItem.get(lineItemEntry.id) ?? 0);
  }
  return { placed, total };
};

// Reads the static inline enum options declared on the `unit_type` field, mirroring
// getOptionsFromUIConfigEnum but scoped to a fields array so callers can pass the section's
// fields directly. A dynamic (options_lookup_field) or absent field yields no options.
const getUnitTypeEnumOptions = (
  fields: ui.TransportUnitsSection['fields'],
): ui.EnumerableFieldTypeConfigWithValues['options'] => {
  const field = fields.find((candidate) => doesPathMatchField(candidate, 'unit_type'));
  if (field && 'type_config' in field && field.type_config && 'options' in field.type_config) {
    return field.type_config.options;
  }
  return [];
};

const findTransportUnitNode = (
  nodes: Array<TransportUnitNode>,
  tuId: TransportUnitId,
): TransportUnitNode | undefined => {
  for (const transportUnit of nodes) {
    if (transportUnit.id === tuId) {
      return transportUnit;
    }
    const found = findTransportUnitNode(transportUnit.children, tuId);
    if (found) {
      return found;
    }
  }
  return undefined;
};

const addContent = (transportUnit: TransportUnitNode, lineItemId: LineItemId, quantity: number): void => {
  const existing = transportUnit.contents.find((content) => content.lineItemId === lineItemId);
  if (existing) {
    existing.quantity += quantity;
  } else {
    transportUnit.contents.push({ lineItemId, quantity });
  }
};

const makeNode = (
  id: TransportUnitId,
  unitType: string,
  contents: TransportUnitNode['contents'],
): TransportUnitNode => ({
  id,
  unitType,
  sscc: undefined,
  contents,
  children: [],
});

const removeTransportUnitNode = (nodes: Array<TransportUnitNode>, tuId: TransportUnitId): boolean => {
  const index = nodes.findIndex((node) => node.id === tuId);
  if (index !== -1) {
    nodes.splice(index, 1);
    return true;
  }
  return nodes.some((node) => removeTransportUnitNode(node.children, tuId));
};

const moveItem = (
  transportUnits: Array<TransportUnitNode>,
  action: Extract<PackingAction, { type: 'MOVE_ITEM' }>,
): void => {
  if (action.fromTuId === action.toTuId) {
    return;
  }
  const source = findTransportUnitNode(transportUnits, action.fromTuId);
  const target = findTransportUnitNode(transportUnits, action.toTuId);
  if (!source || !target) {
    return;
  }
  const content = source.contents.find((item) => item.lineItemId === action.lineItemId);
  if (!content) {
    return;
  }
  const moveQuantity = Math.min(action.quantity, content.quantity);
  if (moveQuantity <= 0) {
    return;
  }
  content.quantity -= moveQuantity;
  if (content.quantity <= 0) {
    source.contents = source.contents.filter((item) => item.lineItemId !== action.lineItemId);
  }
  addContent(target, action.lineItemId, moveQuantity);
};
